import { c as _c } from "react/compiler-runtime";
import { track } from "@opensea/analytics";
import { useCurrencies } from "@opensea/app-kit";
import { bn } from "@opensea/utils";
import { extractChainFromFlowState, useFlowEventHandlers } from "@opensea/wallet";
import { useEffect } from "react";
import { useCoinbaseOneBannerEnabled, useIsChestReminderBannerEnabled } from "@/hooks/use-flags";
import { Sound, usePlaySound } from "@/hooks/use-play-sound";
function extractTokensInfo(items) {
  return items.map(item => ({
    contractAddress: item.contractAddress,
    tokenId: item.tokenId,
    chain: item.chain?.identifier,
    quantity: item.quantity
  }));
}
export function useFlowTracking() {
  const $ = _c(15);
  const {
    addFlowEventHandler
  } = useFlowEventHandlers();
  const {
    getUsdPrice
  } = useCurrencies();
  const [playBuySuccess] = usePlaySound(Sound.BuySuccess);
  const [playListSuccess] = usePlaySound(Sound.ListSuccess);
  const [playSwapSuccess] = usePlaySound(Sound.SwapSuccess);
  const [playAcceptOfferSuccess] = usePlaySound(Sound.AcceptOfferSuccess);
  const [playCreateOfferSuccess] = usePlaySound(Sound.CreateOfferSuccess);
  const [playCancelOrderSuccess] = usePlaySound(Sound.CancelOrderSuccess);
  const [playTransferNftSuccess] = usePlaySound(Sound.TransferNft);
  const [playSendCurrencySuccess] = usePlaySound(Sound.SendCurrencySuccess);
  const [playMint] = usePlaySound(Sound.Mint);
  const coinbaseOneBannerEnabled = useCoinbaseOneBannerEnabled();
  const chestReminderBannerEnabled = useIsChestReminderBannerEnabled();
  let t0;
  let t1;
  if ($[0] !== addFlowEventHandler || $[1] !== chestReminderBannerEnabled || $[2] !== coinbaseOneBannerEnabled || $[3] !== getUsdPrice || $[4] !== playAcceptOfferSuccess || $[5] !== playBuySuccess || $[6] !== playCancelOrderSuccess || $[7] !== playCreateOfferSuccess || $[8] !== playListSuccess || $[9] !== playMint || $[10] !== playSendCurrencySuccess || $[11] !== playSwapSuccess || $[12] !== playTransferNftSuccess) {
    t0 = () => addFlowEventHandler({
      onError: (flow, errorData) => {
        if (flow.type === "swap" && errorData) {
          const swapReceipt = errorData;
          track("Swap Error", {
            fromCurrency: {
              symbol: swapReceipt.fromAsset.symbol,
              contractAddress: swapReceipt.fromAsset.contractAddress,
              amount: swapReceipt.fromQuantity,
              chain: swapReceipt.fromAsset.chain
            },
            toCurrency: {
              symbol: swapReceipt.toAsset.symbol,
              contractAddress: swapReceipt.toAsset.contractAddress,
              amount: swapReceipt.toQuantity,
              chain: swapReceipt.toAsset.chain
            },
            fromAmount: swapReceipt.fromQuantity,
            toAmount: swapReceipt.toQuantity,
            fromAmountUsd: bn(swapReceipt.fromQuantity).times(swapReceipt.fromAsset.usdPrice).toNumber(),
            toAmountUsd: bn(swapReceipt.toQuantity).times(swapReceipt.toAsset.usdPrice).toNumber(),
            transaction: {
              hash: swapReceipt.transactionIdentifier.hash,
              chain: swapReceipt.transactionIdentifier.chain
            },
            failReason: swapReceipt.failReason,
            crossChainRefunded: swapReceipt.crossChainRefunded,
            coinbaseOneBannerEnabled,
            chestReminderBannerEnabled
          });
        }
      },
      onStart: _temp,
      onSuccess: flow_1 => {
        bb42: switch (flow_1.type) {
          case "checkout":
            {
              track("Purchase Success", {
                itemCount: flow_1.items.length,
                crossChain: flow_1.crossChain,
                isSweep: Boolean(flow_1.slippage),
                transaction: {
                  hash: flow_1.transactionIdentifier?.transactionHash,
                  chain: flow_1.transactionIdentifier?.chain
                },
                paymentMethod: flow_1.paymentMethod,
                usdValue: flow_1.totalUsdPrice,
                tokens: extractTokensInfo(flow_1.items),
                coinbaseOneBannerEnabled,
                chestReminderBannerEnabled
              });
              playBuySuccess();
              break bb42;
            }
          case "accept-offer":
            {
              track("Offer Accept Success", {
                itemCount: flow_1.items.length,
                usdValue: flow_1.totalUsdPrice,
                transaction: {
                  hash: flow_1.transactionIdentifier?.transactionHash,
                  chain: flow_1.transactionIdentifier?.chain
                }
              });
              playAcceptOfferSuccess();
              break bb42;
            }
          case "listing":
            {
              track("Listing Success", {
                itemCount: flow_1.items.length
              });
              playListSuccess();
              break bb42;
            }
          case "create-offer":
            {
              track("Offer Create Success");
              playCreateOfferSuccess();
              break bb42;
            }
          case "create-collection-offer":
            {
              track("Collection Offer Create Success", {
                trait: flow_1.trait,
                traits: flow_1.traits
              });
              playCreateOfferSuccess();
              break bb42;
            }
          case "cancel-order":
            {
              track("Order Cancel Success");
              playCancelOrderSuccess();
              break bb42;
            }
          case "cancel-all-orders":
            {
              track("Cancel All Orders Success");
              playCancelOrderSuccess();
              break bb42;
            }
          case "transfer":
            {
              track("NFT Transfer Success", {
                itemCount: flow_1.items.length
              });
              playTransferNftSuccess();
              break bb42;
            }
          case "swap":
            {
              playSwapSuccess();
              break bb42;
            }
          case "connect":
            {
              track("Connect Success");
              break bb42;
            }
          case "siwe":
            {
              track("SIWE Success");
              break bb42;
            }
          case "send":
            {
              track("ERC20 Transfer Success", {
                currency: {
                  symbol: flow_1.currency?.symbol,
                  contractAddress: flow_1.currency?.contractAddress,
                  amount: flow_1.amount
                },
                amount: flow_1.amount,
                amountUsd: flow_1.currency && flow_1.amount ? getUsdPrice(flow_1.currency, flow_1.amount) : undefined
              });
              playSendCurrencySuccess();
              break bb42;
            }
          case "view-wallet":
            {
              break bb42;
            }
          case "mint":
            {
              track("Mint Success", {
                itemCount: flow_1.tokenInputs.length,
                crossChain: flow_1.crossChain
              });
              playMint();
              break bb42;
            }
          default:
            {}
        }
      },
      onEnd: _temp2
    });
    t1 = [addFlowEventHandler, getUsdPrice, playAcceptOfferSuccess, playBuySuccess, playCancelOrderSuccess, playCreateOfferSuccess, playListSuccess, playSendCurrencySuccess, playSwapSuccess, playTransferNftSuccess, playMint, coinbaseOneBannerEnabled, chestReminderBannerEnabled];
    $[0] = addFlowEventHandler;
    $[1] = chestReminderBannerEnabled;
    $[2] = coinbaseOneBannerEnabled;
    $[3] = getUsdPrice;
    $[4] = playAcceptOfferSuccess;
    $[5] = playBuySuccess;
    $[6] = playCancelOrderSuccess;
    $[7] = playCreateOfferSuccess;
    $[8] = playListSuccess;
    $[9] = playMint;
    $[10] = playSendCurrencySuccess;
    $[11] = playSwapSuccess;
    $[12] = playTransferNftSuccess;
    $[13] = t0;
    $[14] = t1;
  } else {
    t0 = $[13];
    t1 = $[14];
  }
  useEffect(t0, t1);
}
function _temp2(flow_2) {
  bb106: switch (flow_2.type) {
    case "checkout":
      {
        track("Purchase End", {
          itemCount: flow_2.items.length,
          crossChain: flow_2.crossChain,
          tokens: extractTokensInfo(flow_2.items)
        });
        break bb106;
      }
    case "accept-offer":
      {
        track("Offer Accept End", {
          itemCount: flow_2.items.length
        });
        break bb106;
      }
    case "listing":
      {
        track("Listing End", {
          itemCount: flow_2.items.length
        });
        break bb106;
      }
    case "create-offer":
      {
        track("Offer Create End");
        break bb106;
      }
    case "create-collection-offer":
      {
        track("Collection Offer Create End");
        break bb106;
      }
    case "cancel-order":
      {
        track("Order Cancel End");
        break bb106;
      }
    case "cancel-all-orders":
      {
        track("Cancel All Orders End");
        break bb106;
      }
    case "transfer":
      {
        track("NFT Transfer End", {
          itemCount: flow_2.items.length
        });
        break bb106;
      }
    case "swap":
      {
        track("Swap End");
        break bb106;
      }
    case "connect":
      {
        track("Connect End");
        break bb106;
      }
    case "siwe":
      {
        track("SIWE End");
        break bb106;
      }
    case "send":
      {
        track("ERC20 Transfer End");
        break bb106;
      }
    case "view-wallet":
      {
        track("View Wallet End");
        break bb106;
      }
    case "mint":
      {
        track("Mint End", {
          itemCount: flow_2.tokenInputs.length,
          crossChain: flow_2.crossChain
        });
        break bb106;
      }
    default:
      {}
  }
}
function _temp(flow_0) {
  const chain = extractChainFromFlowState(flow_0);
  track("Wallet Flow Start", {
    flow: flow_0,
    chain
  });
  bb10: switch (flow_0.type) {
    case "checkout":
      {
        track("Purchase Start", {
          itemCount: flow_0.items.length,
          tokens: extractTokensInfo(flow_0.items)
        });
        break bb10;
      }
    case "accept-offer":
      {
        track("Offer Accept Start", {
          itemCount: flow_0.items.length
        });
        break bb10;
      }
    case "listing":
      {
        track("Listing Start", {
          itemCount: flow_0.items.length
        });
        break bb10;
      }
    case "create-offer":
      {
        track("Offer Create Start");
        break bb10;
      }
    case "create-collection-offer":
      {
        track("Collection Offer Create Start");
        break bb10;
      }
    case "cancel-order":
      {
        track("Order Cancel Start");
        break bb10;
      }
    case "cancel-all-orders":
      {
        track("Cancel All Orders Start");
        break bb10;
      }
    case "transfer":
      {
        track("NFT Transfer Start", {
          itemCount: flow_0.items.length
        });
        break bb10;
      }
    case "swap":
      {
        track("Swap Start");
        break bb10;
      }
    case "connect":
      {
        track("Connect Start");
        break bb10;
      }
    case "siwe":
      {
        track("SIWE Start");
        break bb10;
      }
    case "send":
      {
        track("ERC20 Transfer Start");
        break bb10;
      }
    case "view-wallet":
      {
        track("View Wallet Start");
        break bb10;
      }
    case "mint":
      {
        track("Mint Start", {
          itemCount: flow_0.tokenInputs.length,
          crossChain: flow_0.crossChain
        });
        break bb10;
      }
    default:
      {}
  }
}